import { DestroyRef, effect, inject, Injectable, Injector, signal } from '@angular/core';
import {
  AppEvent,
  AppEventTypes,
  AppId,
  BroadcastService,
  PageSyncState,
  ProcessingTimerSyncState,
  SharedWorkerService,
  SignalRService,
} from '@ecam/eventq-components';
import {
  openSiblingAppWindow,
  RESOLVE_SYNC_WINDOW_NAME,
  siblingAppHref,
} from '../utils/sibling-app-href';
import { environment } from '../../../environments/environment';
import { BrowserTabService } from './browser-tab.service';
import { Observable, Subject } from 'rxjs';
import { AuthSessionService } from './auth-session.service';

interface EventListener {
  name: 'sharedWorker' | 'broadcastChannel' | 'signalr';
  close: () => void;
}

@Injectable({ providedIn: 'root' })
export class AgentEventsService {
  private readonly agentId = crypto.randomUUID();
  private readonly features = {
    signalr: environment.features.signalr,
    sharedWorker: environment.features.sharedWorker,
    broadcastChannel: environment.features.broadcastChannel,
  };
  private readonly shared = inject(SharedWorkerService);
  private readonly broadcast = inject(BroadcastService<AppEvent>);
  private readonly signalr = inject(SignalRService);
  private readonly browserTabs = inject(BrowserTabService);
  private readonly auth = inject(AuthSessionService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  private readonly connected = signal(false);
  private readonly eventsChannel = 'app-events';
  private signalrListener?: EventListener;
  private broadcastListener?: EventListener;
  private sharedListener?: EventListener;

  private readonly eventSubject = new Subject<AppEvent>();
  /** Events observable, emits all app events */
  public get events$(): Observable<AppEvent> {
    return this.eventSubject.asObservable();
  }

  /** On init connect to broadcast channel and shared worker */
  constructor() {
    // Connect SignalR on authentication
    effect(() => {
      const authenticated = this.auth.isAuthenticated();
      if (authenticated) this.connect();
      else this.disconnect();
    });
    // Always keep broadcast listener/shared worker connected, disconnect on destroy
    this.listenForBroadcast();
    this.connectSharedWorker();
    this.destroyRef.onDestroy(() => {
      this.sharedListener?.close();
      this.broadcastListener?.close();
    });
  }

  /**
   * Opens (or focuses) the sibling Resolve app in a named window.
   * Called only from the Sync CTA — never automatically on state events.
   */
  async openSiblingApp(
    _resolveUrl?: string,
    options: { stealFocus?: boolean } = {},
  ): Promise<void> {
    if (!this.features.sharedWorker) return;
    const alreadyOpen = await this.shared.isAppOpen(AppId.RESOLVE).catch(() => false);
    if (alreadyOpen) {
      this.broadcastSiblingFocus();
      return;
    }
    const resolveUrl = siblingAppHref(environment.resolveAppBaseUrl, '/', '/resolve');
    openSiblingAppWindow(resolveUrl, RESOLVE_SYNC_WINDOW_NAME, {
      popout: environment.features.enableTabPopout,
      stealFocus: options.stealFocus,
    });
  }

  /** Ask an already-open sibling tab to take focus (no new window). */
  broadcastSiblingFocus(): void {
    this.broadcastEvent({ type: 'SIBLING_FOCUS' as AppEvent['type'] });
  }

  /** Requests main SignalR hub, limited to one per user */
  private requestSignalR() {
    if (!this.features.signalr) return;
    if (this.signalrListener) return;
    const close = this.signalr.requestAppEventsSignalR((e) => this.onServerEvent(e));
    this.signalrListener = { name: 'signalr', close };
  }

  /** Connect to shared worker for tracking connected apps */
  private connectSharedWorker() {
    if (!this.features.sharedWorker) return;
    if (this.sharedListener) return;
    this.shared.connect();
    this.sharedListener = {
      name: 'sharedWorker',
      close: () => this.shared.disconnect(),
    };
  }

  /** Listens to broadcastChannel, handles any sent events */
  private listenForBroadcast() {
    if (!this.features.broadcastChannel) return;
    if (this.broadcastListener) return;
    const close = this.broadcast.listen(this.eventsChannel, (e) => this.handleEvent(e));
    this.broadcastListener = { close, name: 'broadcastChannel' };
  }

  /** Handles SignalR event including broadcast */
  private onServerEvent(event: AppEvent) {
    this.broadcastEvent(event);
    this.handleEvent(event);
  }
  /** Handles general event functionality. Emits event or handles directly */
  private handleEvent(event: AppEvent) {
    if (event.agentId === this.agentId) return; // Skip events from same instance

    // Session login must reach the login page before auth is restored.
    if (event.type === AppEventTypes.SESSION_LOGIN) {
      void this.auth.applyRemoteSession();
      return;
    }
    if (event.type === AppEventTypes.SESSION_LOGOUT) {
      this.auth.applyRemoteLogout();
      return;
    }

    /** Skip if not authenticated. Some events may be allowed through */
    if (!this.auth.isAuthenticated()) return;
    if (event.type === AppEventTypes.SIBLING_FOCUS) {
      window.focus();
      return;
    }
    switch (event.type) {
      case AppEventTypes.STATE_QUERY:
        this.broadcastStateSync({ openResolve: false });
        break;
    }
    this.eventSubject.next(event);
  }

  /** Request SignalR connection */
  public connect() {
    if (this.connected()) return;
    this.requestSignalR();
    this.connected.set(true);
  }
  /** Disconnect from SignalR */
  public disconnect() {
    if (!this.connected()) return;
    this.signalrListener?.close();
    delete this.signalrListener;
    this.connected.set(false);
  }

  private broadcastEvent(event: Omit<AppEvent, 'agentId'>) {
    if (!this.auth.isAuthenticated()) return;
    this.broadcast.broadcast(this.eventsChannel, {
      ...event,
      agentId: this.agentId,
    });
  }

  /**
   * Broadcasts a STATE_QUERY so sibling apps respond with their current state.
   * Does NOT auto-open Resolve — use Sync CTA (`openSiblingApp`) for that.
   */
  async broadcastStateQuery(options: { openResolve?: boolean } = {}) {
    if (options.openResolve) {
      this.openSiblingApp(undefined, { stealFocus: false }).catch(() => undefined);
    }
    this.broadcastEvent({ type: AppEventTypes.STATE_QUERY });
  }

  /**
   * Syncs current EventQ workspace state to Resolve.
   * Does NOT auto-open Resolve — use Sync CTA (`openSiblingApp`) for that.
   */
  async broadcastStateSync(options: { openResolve?: boolean } = {}) {
    if (options.openResolve) {
      this.openSiblingApp().catch(() => undefined);
    }
    this.broadcastEvent({
      type: AppEventTypes.STATE_SYNC,
      pageState: this.buildEnrichedPageState(),
    });
  }

  broadcastTimerSync(processingTimer: ProcessingTimerSyncState): void {
    this.broadcastEvent({ type: AppEventTypes.TIMER_SYNC, processingTimer });
  }

  broadcastTimerClear(processingTimer: ProcessingTimerSyncState): void {
    this.broadcastEvent({ type: AppEventTypes.TIMER_CLEAR, processingTimer });
  }

  /** Notify sibling apps that this window signed in (shared session is already persisted). */
  broadcastSessionLogin(): void {
    this.broadcastEvent({ type: AppEventTypes.SESSION_LOGIN });
  }

  /** Notify sibling apps that this window signed out (shared session is already cleared). */
  broadcastSessionLogout(): void {
    this.broadcastEvent({ type: AppEventTypes.SESSION_LOGOUT });
  }

  broadcastEventClosed(eventId: string, label: string): void {
    this.broadcastEvent({ type: AppEventTypes.EVENT_CLOSED, id: eventId, label });
  }

  broadcastEventParked(eventId: string, label: string, durationLabel: string): void {
    this.broadcastEvent({ type: AppEventTypes.EVENT_PARKED, id: eventId, label, durationLabel });
  }

  private buildEnrichedPageState(): PageSyncState {
    return this.browserTabs.pageState();
  }
}
